/**
 * Drives the document lifecycle: reduce → effects → I/O → follow-up commands.
 *
 * The reducer decides, the store performs, and the controller closes the loop
 * between them. Two rules keep the loop safe:
 *
 * 1. **Every store rejection becomes a `save-failed` command.** A thrown error
 *    that escaped this loop would skip the reducer, and skipping the reducer is
 *    how the working copy would get released while edits were still unsaved —
 *    exactly what §13 forbids.
 * 2. **Confirmations are asked, never assumed.** `confirm-overwrite` and
 *    `confirm-replace-existing` go to the {@link ConfirmationPort}; a `false`
 *    answer simply ends the sequence with the original untouched (§4.10).
 */
import { reduce } from './machine.js';
import type { Command, Effect, RecoveryPolicy, Transition } from './machine.js';
import { DEFAULT_RECOVERY_POLICY } from './machine.js';
import type { DocumentSession, SaveFailureReason, SavedRef } from './types.js';
import { isDirty } from './types.js';
import type {
  BytesProvider, ConfirmationPort, FileStore, NotificationPort,
} from './ports.js';
import { FileStoreError } from './ports.js';

export interface ControllerDeps {
  readonly store: FileStore;
  readonly confirm: ConfirmationPort;
  readonly notify: NotificationPort;
  /** Supplies current document bytes. */
  readonly bytes: BytesProvider;
  /** Monotonic clock, injected so tests are deterministic. */
  readonly now: () => number;
  readonly policy?: RecoveryPolicy;
}

/** One step of the loop, kept for diagnostics and for the §31 screen. */
export interface ControllerStep {
  readonly command: Command;
  readonly effects: readonly Effect[];
  readonly followUps: readonly Command[];
}

export class DocumentController {
  #session: DocumentSession;
  readonly #deps: ControllerDeps;
  readonly #policy: RecoveryPolicy;
  readonly #history: ControllerStep[] = [];
  /** Serialises dispatches so two saves cannot interleave mid-sequence. */
  #queue: Promise<void> = Promise.resolve();

  constructor(session: DocumentSession, deps: ControllerDeps) {
    this.#session = session;
    this.#deps = deps;
    this.#policy = deps.policy ?? DEFAULT_RECOVERY_POLICY;
  }

  get session(): DocumentSession {
    return this.#session;
  }

  get history(): readonly ControllerStep[] {
    return this.#history;
  }

  /**
   * Apply a command and everything that follows from it.
   *
   * Runs to quiescence: a command produces effects, effects produce follow-up
   * commands, and those are applied in turn until nothing new is generated.
   */
  async dispatch(command: Command): Promise<DocumentSession> {
    const run = this.#queue.then(() => this.#dispatchNow(command));
    // Keep the chain alive even if this dispatch rejects, so a later command is
    // not silently dropped behind a poisoned promise.
    this.#queue = run.then(() => undefined, () => undefined);
    await run;
    return this.#session;
  }

  async #dispatchNow(command: Command): Promise<void> {
    let pending: Command[] = [command];
    let guard = 0;

    while (pending.length > 0) {
      // A malformed effect chain must not spin forever; 64 is far above any
      // legitimate sequence (edit → flush → recover → prune is four).
      if (++guard > 64) {
        throw new Error('document controller did not settle after 64 steps');
      }
      const next: Command[] = [];
      for (const cmd of pending) {
        const transition: Transition = reduce(this.#session, cmd, this.#policy);
        this.#session = transition.session;
        const followUps = await this.#perform(transition.effects);
        this.#history.push({ command: cmd, effects: transition.effects, followUps });
        next.push(...followUps);
      }
      pending = next;
    }
  }

  async #perform(effects: readonly Effect[]): Promise<Command[]> {
    const followUps: Command[] = [];
    for (const effect of effects) {
      const produced = await this.#performOne(effect);
      followUps.push(...produced);
    }
    return followUps;
  }

  async #performOne(effect: Effect): Promise<Command[]> {
    const { store, confirm, bytes, now } = this.#deps;
    const notify = (messageKey: string, detail?: string): void => {
      this.#deps.notify.notify(messageKey, detail);
    };
    const session = this.#session;

    switch (effect.kind) {
      case 'create-working-copy': {
        try {
          const ref = await store.createWorkingCopy(effect.from, session.id);
          // Seed the working copy with the original's bytes so a recovery from
          // it is meaningful even before the first edit.
          await store.writeWorkingCopy(ref, await store.readAll(effect.from.handle));
          return [{ kind: 'working-copy-ready', ref }];
        } catch (err) {
          return [{ kind: 'working-copy-failed', reason: classify(err) }];
        }
      }

      case 'flush-working-copy': {
        if (!session.workingCopy) return [];
        try {
          await store.writeWorkingCopy(session.workingCopy, await bytes(effect.revision));
        } catch (err) {
          // A failed flush is not a failed save — there is nothing to roll back,
          // and the next flush will try again. Tell the user and carry on.
          notify('error.editsSavedTemporarily', String((err as Error).message));
        }
        return [];
      }

      case 'write-recovery-point': {
        try {
          const point = await store.writeRecoveryPoint(
            session.id, effect.revision, await bytes(effect.revision), effect.atMs,
          );
          return [{ kind: 'recovery-point-written', point }];
        } catch (err) {
          notify('error.editsSavedTemporarily', String((err as Error).message));
          return [];
        }
      }

      case 'prune-recovery-points': {
        try {
          await store.deleteRecoveryPoints(effect.drop);
        } catch {
          // Housekeeping. Leaving a stale snapshot behind costs disk space;
          // failing the user's save over it would cost their work.
        }
        return [];
      }

      case 'confirm-overwrite': {
        const approved = await confirm.confirmOverwriteOriginal(session.original);
        // Declining leaves the session exactly where it was: still editing,
        // original untouched.
        return approved ? [{ kind: 'overwrite-confirmed', atMs: now() }] : [];
      }

      case 'confirm-replace-existing': {
        const approved = await confirm.confirmReplaceExisting(effect.name);
        return approved ? [{ kind: 'replace-existing-confirmed', atMs: now() }] : [];
      }

      case 'warn-loss': {
        await confirm.acknowledgeLoss(effect.items);
        return [];
      }

      case 'write-new-file': {
        try {
          const payload = await bytes(effect.revision);
          if (await store.exists(effect.suggestedName)) {
            // §13: never replace a different existing file silently.
            return [{ kind: 'save-failed', reason: 'name-conflict' }];
          }
          const outcome = await store.saveAs(payload, effect.suggestedName, effect.revision);
          return outcome.ok
            ? [{ kind: 'save-succeeded', ref: outcome.ref }]
            : [{ kind: 'save-failed', reason: outcome.reason }];
        } catch (err) {
          return [{ kind: 'save-failed', reason: classify(err) }];
        }
      }

      case 'write-over-original': {
        try {
          const payload = await bytes(effect.revision);
          const outcome = await store.overwriteOriginal(session.original, payload, effect.revision);
          return outcome.ok
            ? [{ kind: 'save-succeeded', ref: outcome.ref }]
            : [{ kind: 'save-failed', reason: outcome.reason }];
        } catch (err) {
          return [{ kind: 'save-failed', reason: classify(err) }];
        }
      }

      case 'validate-export': {
        // The handler owns format-specific validation; the controller only
        // records that it is expected. `markValidated` closes the loop.
        return [];
      }

      case 'schedule-retry': {
        // Timers belong to the shell, which knows about app lifecycle and can
        // cancel them. `retryNow()` is the entry point back into the loop.
        notify('error.editsSavedTemporarily', `retry in ${effect.delayMs}ms`);
        return [];
      }

      case 'notify': {
        notify(effect.messageKey);
        return [];
      }

      case 'release-working-copy': {
        // Belt and braces: the reducer only emits this when nothing is dirty,
        // but deleting a user's only copy of their edits is unrecoverable, so
        // the condition is checked again at the point of deletion.
        if (isDirty(session)) {
          notify('error.editsSavedTemporarily', 'refused to release a dirty working copy');
          return [];
        }
        try {
          await store.releaseWorkingCopy(effect.ref);
        } catch { /* the sweep will collect it later */ }
        return [];
      }

      case 'keep-working-copy': {
        notify('error.editsSavedTemporarily', effect.reason);
        return [];
      }

      default: {
        const never: never = effect;
        void never;
        return [];
      }
    }
  }

  /** Retry a failed save. Called by the shell when its backoff timer fires. */
  async retryNow(): Promise<DocumentSession> {
    return this.dispatch({ kind: 'retry-save', atMs: this.#deps.now() });
  }

  /**
   * Record that the exported file was reopened and checked (§13, §42-27).
   *
   * Kept separate from `save-succeeded` because validation is asynchronous and
   * format-specific: the save is complete, but until this is called the file
   * has not been *verified*, and the §31 screen says so.
   */
  markValidated(ok: boolean): void {
    const last = this.#session.lastSaved;
    if (!last) return;
    const validated: SavedRef = { ...last, validated: ok };
    this.#session = { ...this.#session, lastSaved: validated };
  }
}

/** Map an arbitrary rejection onto a reason the reducer understands. */
function classify(err: unknown): SaveFailureReason {
  if (err instanceof FileStoreError) return err.reason;
  const message = String((err as Error)?.message ?? err).toLowerCase();
  if (message.includes('enospc') || message.includes('no space')) return 'storage-full';
  if (message.includes('eacces') || message.includes('eperm') || message.includes('permission')) {
    return 'permission-denied';
  }
  if (message.includes('expired')) return 'permission-expired';
  if (message.includes('eexist') || message.includes('exists')) return 'name-conflict';
  if (message.includes('abort') || message.includes('cancel')) return 'cancelled';
  return 'io-error';
}

/**
 * Recoverable work left behind by a session that never finished.
 *
 * §23 requires crash recovery to be offered, which means finding these after a
 * restart — a process that died mid-edit had no chance to clean up.
 */
export interface RecoverableWork {
  readonly sessionId: string;
  readonly latest: import('./types.js').RecoveryPoint;
  readonly pointCount: number;
  readonly ageMs: number;
}

export async function findRecoverableWork(
  store: FileStore, nowMs: number,
): Promise<RecoverableWork[]> {
  const points = await store.listRecoveryPoints();
  const bySession = new Map<string, import('./types.js').RecoveryPoint[]>();
  for (const point of points) {
    const sessionId = point.id.split(':')[0] ?? point.id;
    const list = bySession.get(sessionId) ?? [];
    list.push(point);
    bySession.set(sessionId, list);
  }

  return [...bySession.entries()].map(([sessionId, list]) => {
    const sorted = [...list].sort((a, b) => b.createdAtMs - a.createdAtMs);
    const latest = sorted[0]!;
    return {
      sessionId,
      latest,
      pointCount: sorted.length,
      ageMs: nowMs - latest.createdAtMs,
    };
  }).sort((a, b) => a.ageMs - b.ageMs);
}
